import { useCallback, useState } from 'react';
import { type Schema, ValidationError } from 'yup';
import type { FieldChangeEvent } from '../../types';

// Field name -> first validation message for that field.
export type ValidationErrors = Record<string, string>;

const useValidation = (fields: unknown, schema: Schema) => {
  const [errors, setErrors] = useState<ValidationErrors>({});

  const validate = useCallback(() => {
    const errorMessages: ValidationErrors = {};

    try {
      schema.validateSync(fields, { abortEarly: false });
    } catch (e) {
      if (!(e instanceof ValidationError)) throw e;

      e.inner.forEach(error => {
        if (error.path) {
          errorMessages[error.path] = error.message;
        }
      });
    }

    setErrors(errorMessages);
  }, [fields, schema]);

  const handleChange = useCallback(
    (event: FieldChangeEvent<unknown>) => {
      const { name, value } = event.currentTarget;
      const sanitizedName = name.replace('[]', '');

      const values = {
        ...(fields as object),
        [sanitizedName]: value,
      };

      try {
        schema.validateSyncAt(sanitizedName, values);

        setErrors(prev =>
          Object.keys(prev)
            .filter(key => key !== sanitizedName)
            .reduce<ValidationErrors>(
              (obj, key) => ({ ...obj, [key]: prev[key] }),
              {}
            )
        );
      } catch (error) {
        setErrors(prev => ({
          ...prev,
          [name]: error instanceof Error ? error.message : String(error),
        }));
      }
    },
    [fields, schema]
  );

  const handleBlur = handleChange;

  const isValid = useCallback(
    () => schema.isValidSync(fields),
    [fields, schema]
  );

  return {
    errors,
    setErrors,
    validate,
    handleBlur,
    handleChange,
    isValid,
  };
};

export default useValidation;
